/**
 * Matrix reasoning — a grid with one cell missing and a row of answers.
 *
 * The spec is self-checking: the blank cell carries the true content (the
 * renderer hides it), and `correctIndex` must point at the option that matches
 * it exactly. The declared varying dimensions must genuinely vary across the
 * grid, and every other dimension must be constant — otherwise the puzzle has
 * more than one defensible answer.
 */

import { z } from 'zod'
import { ColorTokenSchema, EmojiSchema, SentenceSchema, isUnique, specFor } from '../common'

export const MATRIX_DIMENSIONS = ['shape', 'color', 'size', 'count', 'rotation'] as const
export type MatrixDimension = (typeof MATRIX_DIMENSIONS)[number]

export const MATRIX_SHAPES = ['circle', 'square', 'triangle', 'star', 'heart', 'hexagon', 'diamond'] as const
export const MATRIX_SIZES = ['small', 'medium', 'large'] as const

export const MatrixCellContentSchema = z.object({
  shape: z.enum(MATRIX_SHAPES),
  colorToken: ColorTokenSchema,
  size: z.enum(MATRIX_SIZES),
  count: z.number().int().min(1).max(5),
  rotationDeg: z.number().min(0).max(359),
  /** Themed stand-in drawn instead of the plain shape. */
  emoji: EmojiSchema.optional(),
})

export type MatrixCellContent = z.infer<typeof MatrixCellContentSchema>

export const MatrixCellSchema = MatrixCellContentSchema.extend({
  /** Exactly one cell is blank — the one she solves for. */
  blank: z.boolean().default(false),
})

const DIMENSION_VALUE: Record<MatrixDimension, (cell: MatrixCellContent) => string | number> = {
  shape: (cell) => cell.shape,
  color: (cell) => cell.colorToken,
  size: (cell) => cell.size,
  count: (cell) => cell.count,
  rotation: (cell) => cell.rotationDeg,
}

function sameContent(a: MatrixCellContent, b: MatrixCellContent): boolean {
  return (
    a.shape === b.shape &&
    a.colorToken === b.colorToken &&
    a.size === b.size &&
    a.count === b.count &&
    a.rotationDeg === b.rotationDeg &&
    (a.emoji ?? '') === (b.emoji ?? '')
  )
}

function contentKey(cell: MatrixCellContent): string {
  return [cell.shape, cell.colorToken, cell.size, cell.count, cell.rotationDeg, cell.emoji ?? ''].join('|')
}

export const MatrixReasoningConfigSchema = z
  .object({
    gridSize: z.enum(['2x2', '3x3']),
    varyingDimensions: z.array(z.enum(MATRIX_DIMENSIONS)).min(1).max(3),
    /** Row-major. Length must match the grid. */
    cells: z.array(MatrixCellSchema).min(4).max(9),
    options: z.array(MatrixCellContentSchema).min(2).max(6),
    correctIndex: z.number().int().min(0),
    /** Parent-facing one-liner: what changes as you read across. */
    ruleHint: SentenceSchema.optional(),
  })
  .superRefine((config, ctx) => {
    const expected = config.gridSize === '2x2' ? 4 : 9
    if (config.cells.length !== expected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cells'],
        message: `a ${config.gridSize} grid needs ${expected} cells, got ${config.cells.length}`,
      })
      return
    }
    if (!isUnique(config.varyingDimensions)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['varyingDimensions'], message: 'list each dimension once' })
    }

    const blankIndexes = config.cells.flatMap((cell, index) => (cell.blank ? [index] : []))
    if (blankIndexes.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cells'],
        message: `exactly one cell is the missing one, got ${blankIndexes.length}`,
      })
      return
    }

    if (config.correctIndex >= config.options.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['correctIndex'],
        message: `correctIndex ${config.correctIndex} is outside the ${config.options.length} options`,
      })
      return
    }

    const answer = config.cells[blankIndexes[0]]
    if (!sameContent(answer, config.options[config.correctIndex])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['correctIndex'],
        message: 'the correct option does not match the missing cell',
      })
    }
    if (!isUnique(config.options.map(contentKey))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['options'], message: 'two options are identical' })
    }

    for (const dimension of MATRIX_DIMENSIONS) {
      const values = new Set(config.cells.map((cell) => DIMENSION_VALUE[dimension](cell)))
      const declared = config.varyingDimensions.includes(dimension)
      if (declared && values.size < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['varyingDimensions'],
          message: `"${dimension}" is listed as varying but is the same in every cell`,
        })
      }
      if (!declared && values.size > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['cells'],
          message: `"${dimension}" changes across the grid but is not listed in varyingDimensions`,
        })
      }
    }
  })

export type MatrixReasoningConfig = z.infer<typeof MatrixReasoningConfigSchema>

export const MatrixReasoningSpecSchema = specFor('matrix_reasoning', MatrixReasoningConfigSchema)
